import { CardContent, PageSectionContent } from '@types';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  OnInit,
  Type,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';

import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { AppStateService } from '@services/app-state.service';
import { CarouselSectionComponent } from './carousel-section/carousel-section.component';
import { IndexCardsSectionComponent } from './index-cards-section/index-cards-section.component';
import { Logger } from '@utils/logger';
import { MediaCardsSectionComponent } from './media-cards-section/media-cards-section.component';
import { MediaPanelSectionComponent } from './media-panel-section/media-panel-section.component';
import { SectionComponent } from './section-component.interface';
import { StaggeredSectionComponent } from './staggered-section/staggered-section.component';
import { StaggeredVisualSectionComponent } from './staggered-visual-section/staggered-visual-section.component';
import { ThumbnailsSectionComponent } from './thumbnails-section/thumbnails-section.component';
import { WidenService } from '@services/widen.service';
import { ActivatedRoute } from '@angular/router';
import { LinkService } from '@services/link.service';

/**
 * Logger
 */
const logger = Logger.getLogger('PageSectionComponent');

/**
 * FT Banner component
 *
 * This component is used at the top of a template and has it's own associated document type.
 *
 * Selector: `ft-page-section`
 *
 * Exported as: `PageSectionContent`
 */
@Component({
  selector: 'ft-page-section',
  templateUrl: './page-section.component.html',
  styleUrls: ['./page-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageSectionComponent
  extends AbstractBaseComponent
  implements OnInit {
  @ViewChild('dynamicCmp', {
    read: ViewContainerRef,
    static: false,
  })
  componentContainer;

  /**
   * Component Reference
   */
  componentRef: ComponentRef<SectionComponent>;

  /**
   * Page section document
   */
  content: PageSectionContent;

  /**
   * Debug Mode
   */
  debugMode = false;

  /**
   * A simple map to convert old names to new
   * TODO: Remove in time
   */
  legacyCardTypes = new Map<string, string>([
    ['card_carousel', 'carousel'],
    ['card_index-compact', 'index_cards_compact'],
    ['card_index-compact-centered', 'index_cards_compact_centered'],
    ['card_media-stacked', 'media_cards'],
    ['card_media', 'media_cards_list'],
    ['card_thumbnail', 'thumbnail_cards'],
    ['content_staggered', 'staggered_content'],
    ['card_staggered', 'staggered_cards'],
  ]);

  /**
   * Constructor
   */
  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private changeDetectorRef: ChangeDetectorRef,
    private appStateService: AppStateService,
    private widenService: WidenService,
    private route: ActivatedRoute,
    private linkService: LinkService
  ) {
    super();
  }

  /**
   * Angular init hook
   */
  ngOnInit(): void {
    super.ngOnInit();

    // Set content on init
    this.content = this.getContent();

    // Initialize component based on type
    if (this.content && this.sectionType) {
      this.initDynamicComponent();
    }

    this.route?.queryParams?.subscribe((params) => {
      logger.debug(params);
      this.debugMode = params.padding === 'debug';
      logger.debug(this.debugMode);
    });
  }

  /**
   * Initialise dynamic section component based on
   * type.
   */
  initDynamicComponent(): void {
    // Load component based on cardType
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(
      this.getComponentName()
    );

    this.changeDetectorRef.detectChanges();

    // Clear any existing content
    this.componentContainer?.clear();
    this.componentRef = this.componentContainer.createComponent(
      componentFactory
    );

    // Fallback to legacy setting
    const theme = this.theme || this.background;

    // Populate required inputs
    this.componentRef.instance.populate(
      this.component,
      this.page,
      this.sectionType,
      this.content,
      theme
    );

    this.changeDetectorRef.detectChanges();
  }

  /**
   * Get content from document
   */
  getContent(): PageSectionContent {
    // Read the content from the resourceapi
    const content = this.document?.getData<PageSectionContent>();

    // If content exists
    if (content) {
      // If page section has cards
      if (content.cards) {
        // Format each card into EDS format
        for (const contentCard of content.cards) {
          const card = contentCard as CardContent;
          // EDS expects 'bodyContent'
          if (card.content) {
            card.bodyContent = card.content;
          }
          // Handle Widen assets
          if (card.image && card.image.widenAsset) {
            card.imageSrc = this.widenService.getWidenAssetUrl(
              card.image.widenAsset
            );
            if (card.imageSrc.includes('.svg')) {
              card.isIcon = true;
            }
          }
          // Process Card CTA
          if (card.link?.linkCollection?.length) {
            card.link = card.link.linkCollection[0];
            card.link.displayText = card.link?.displayText;
            card.link.url = this.linkService.getCTALink(
              this.page,
              card.link,
              this.appStateService?.getHomePageUrl()
            );
            // Convery for eds
            card.linkText = card.link?.displayText;
            card.url = card.link?.url;
          }
        }
      }
      // Process Section CTA
      if (content.link?.linkCollection?.length) {
        content.link = content.link.linkCollection[0];
        content.link.displayText = content.link?.displayText;
        content.link.url = this.linkService.getCTALink(
          this.page,
          content.link,
          this.appStateService?.getHomePageUrl()
        );
      }

      // Process section image
      if (content.imageCompound && content.imageCompound.widenAsset) {
        content.imageSrc = this.widenService.getWidenAssetUrl(
          content.imageCompound.widenAsset
        );
      }
    }
    return content;
  }

  /**
   * Load component based on section name
   * TODO: Can we improve this look up?
   */
  getComponentName(): Type<SectionComponent> {
    let sectionComponent;
    switch (this.sectionType) {
      case 'carousel':
        sectionComponent = CarouselSectionComponent;
        break;
      case 'index_cards_compact':
        sectionComponent = IndexCardsSectionComponent;
        break;
      case 'index_cards_compact_centered':
        sectionComponent = IndexCardsSectionComponent;
        break;
      case 'media_cards':
        sectionComponent = MediaCardsSectionComponent;
        break;
      case 'media_cards_list':
        sectionComponent = MediaCardsSectionComponent;
        break;
      case 'media_panel':
        sectionComponent = MediaPanelSectionComponent;
        break;
      case 'staggered_cards':
        sectionComponent = StaggeredSectionComponent;
        break;
      case 'staggered_content':
        sectionComponent = StaggeredSectionComponent;
        break;
      case 'staggered_block_imaged':
        sectionComponent = StaggeredVisualSectionComponent;
        break;
      case 'thumbnail_cards':
        sectionComponent = ThumbnailsSectionComponent;
        break;
      case 'thumbnail_cards_compact':
        sectionComponent = ThumbnailsSectionComponent;
        break;
      default:
        sectionComponent = MediaCardsSectionComponent;
    }
    return sectionComponent;
  }

  get backgroundClass() {
    if (this.background) {
      return 'background' + ' background--' + this.background;
    }
  }

  /**
   * Component Layout
   */
  get layout() {
    return 'two-column';
  }

  /**
   * Get banner type (theme)
   */
  get sectionType() {
    let cardType = this.component
      .getParameters()
      .cardType?.replace(/\s+/g, '_');

    // Convert legacy format
    if (this.legacyCardTypes.has(cardType)) {
      cardType = this.legacyCardTypes.get(cardType);
    }

    return cardType?.toLowerCase();
  }
}
